import { API_BASE } from '../api';

function loadImage(src) {
  return new Promise(function (resolve, reject) {
    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () {
      resolve(img);
    };
    img.onerror = function () {
      resolve(null);
    };
    img.src = src;
  });
}

function avatarColor(username) {
  var hash = 0;
  for (var i = 0; i < username.length; i++) {
    hash = username.charCodeAt(i) + ((hash << 5) - hash);
  }
  var hue = ((hash % 360) + 360) % 360;
  return 'hsl(' + hue + ', 55%, 55%)';
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function truncateText(ctx, text, maxWidth) {
  if (ctx.measureText(text).width <= maxWidth) return text;
  var ellipsis = '...';
  var truncated = text;
  while (truncated.length > 0 && ctx.measureText(truncated + ellipsis).width > maxWidth) {
    truncated = truncated.slice(0, -1);
  }
  return truncated + ellipsis;
}

function extractDominantColor(img) {
  var sampleCanvas = document.createElement('canvas');
  sampleCanvas.width = 50;
  sampleCanvas.height = 50;
  var sCtx = sampleCanvas.getContext('2d');
  sCtx.drawImage(img, 0, 0, 50, 50);
  var data = sCtx.getImageData(0, 0, 50, 50).data;
  var rT = 0,
    gT = 0,
    bT = 0,
    count = 0;
  for (var i = 0; i < data.length; i += 4) {
    rT += data[i];
    gT += data[i + 1];
    bT += data[i + 2];
    count++;
  }
  return {
    r: Math.round(rT / count),
    g: Math.round(gT / count),
    b: Math.round(bT / count),
  };
}

function rgbToHsl(r, g, b) {
  r /= 255;
  g /= 255;
  b /= 255;
  var max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  var h,
    s,
    l = (max + min) / 2;
  if (max === min) {
    h = 0;
    s = 0;
  } else {
    var d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return { h: h * 360, s: s, l: l };
}

// Extract prominent colors from an image — picks 4 diverse colors
export function extractProminentColors(imgSrc) {
  return loadImage(imgSrc).then(function (img) {
    if (!img) return ['#f5f5f5', '#1a1a1a'];

    var size = 100;
    var canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    var ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, size, size);
    var data = ctx.getImageData(0, 0, size, size).data;

    // Quantize into buckets (round to nearest 24)
    var buckets = {};
    for (var i = 0; i < data.length; i += 4) {
      var r = Math.round(data[i] / 24) * 24;
      var g = Math.round(data[i + 1] / 24) * 24;
      var b = Math.round(data[i + 2] / 24) * 24;
      var key = r + ',' + g + ',' + b;
      if (!buckets[key]) buckets[key] = { r: 0, g: 0, b: 0, count: 0 };
      buckets[key].r += data[i];
      buckets[key].g += data[i + 1];
      buckets[key].b += data[i + 2];
      buckets[key].count++;
    }

    // Sort by frequency
    var sorted = Object.values(buckets).sort(function (a, b) {
      return b.count - a.count;
    });

    // Build candidate list with HSL info
    var candidates = [];
    for (var j = 0; j < sorted.length && candidates.length < 20; j++) {
      var c = sorted[j];
      if (c.count < 10) continue; // skip noise
      var cr = Math.round(c.r / c.count);
      var cg = Math.round(c.g / c.count);
      var cb = Math.round(c.b / c.count);
      var hsl = rgbToHsl(cr, cg, cb);
      candidates.push({ r: cr, g: cg, b: cb, h: hsl.h, s: hsl.s, l: hsl.l, count: c.count });
    }

    // Pick 4 diverse colors using hue distance
    var picked = [];
    for (var p = 0; p < candidates.length && picked.length < 4; p++) {
      var cand = candidates[p];
      var tooClose = false;
      for (var k = 0; k < picked.length; k++) {
        var pk = picked[k];
        // Hue distance (circular)
        var hueDist = Math.abs(cand.h - pk.h);
        if (hueDist > 180) hueDist = 360 - hueDist;
        // Also check lightness distance
        var lDist = Math.abs(cand.l - pk.l);
        // If both hue and lightness are close, skip
        if (hueDist < 30 && lDist < 0.15) {
          tooClose = true;
          break;
        }
        // If very close in RGB space, skip
        var rgbDist = Math.abs(cand.r - pk.r) + Math.abs(cand.g - pk.g) + Math.abs(cand.b - pk.b);
        if (rgbDist < 100) {
          tooClose = true;
          break;
        }
      }
      if (!tooClose) {
        picked.push(cand);
      }
    }

    // Darken slightly for background use (0.65 preserves vibrancy)
    var result = picked.map(function (c) {
      var dr = Math.round(c.r * 0.65);
      var dg = Math.round(c.g * 0.65);
      var db = Math.round(c.b * 0.65);
      return 'rgb(' + dr + ',' + dg + ',' + db + ')';
    });

    // Always include white and near-black
    result.push('#f5f5f5');
    result.push('#1a1a1a');

    return result;
  });
}

function drawHeavyRotationLogo(ctx, centerX, y, color, size) {
  var fontSize = size || 40;
  var lightFont =
    '300 italic ' + fontSize + 'px Raleway, -apple-system, BlinkMacSystemFont, sans-serif';
  ctx.fillStyle = color;

  ctx.font = '800 ' + fontSize + 'px Raleway, -apple-system, BlinkMacSystemFont, sans-serif';
  var heavyW = ctx.measureText('Heavy').width;
  ctx.font = lightFont;
  var rotW = ctx.measureText('Rotation').width;
  var appW = ctx.measureText('.app').width;
  var total = heavyW + 4 + rotW + appW;
  var startX = centerX - total / 2;

  ctx.font = '800 ' + fontSize + 'px Raleway, -apple-system, BlinkMacSystemFont, sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText('Heavy', startX, y);
  ctx.font = lightFont;
  ctx.fillText('Rotation', startX + heavyW + 4, y);
  ctx.fillText('.app', startX + heavyW + 4 + rotW, y);
}

// Draws the post card matching the feed layout exactly:
// PostDiv (shadow, radius 12, white bg, no border)
//   UserDiv (avatar + username, small text, tight padding)
//   ArtworkBlock (inset with black border, thick right/bottom, radius 8)
//     MediaHeader (black bg, artist bold, title italic, album)
//     Artwork image
//   PostText (if any)
// Returns the Y position after the card
function drawPostCard(ctx, opts, artImg, profileImg, cardX, cardY, cardW) {
  var artist = opts.artist || '';
  var songTitle = opts.songTitle || '';
  var albumTitle = opts.albumTitle || '';
  var releaseDate = opts.releaseDate || '';
  var username = opts.compact ? '' : opts.username || '';
  var note = opts.compact ? '' : opts.note || '';

  var cardRadius = 24; // 12px at 2x
  var artInset = 16; // 0.5em at ~32px base = ~16px, artwork inset from card edges

  // --- Measure sections ---

  // User row: padding 0.5em top/bottom at ~32px base = ~16px each side
  var userRowH = username ? 56 : 0;

  // MediaHeader
  var headerPad = 24;
  var headerLineH = 36;
  var isSong = songTitle && songTitle.length > 0;
  var title = isSong ? songTitle : albumTitle;
  var year = releaseDate ? releaseDate.slice(0, 4) : '';
  var headerLines = 2;
  if (isSong && albumTitle) headerLines = 3;
  var headerH = headerPad * 2 + headerLines * headerLineH;

  // Artwork (square, inset by artInset on each side)
  var artBlockW = cardW - artInset * 2;
  var artSize = artBlockW; // square

  // Note area (max 2 lines, "more" if truncated — matches feed)
  var maxNoteLines = 2;
  var noteH = 0;
  var noteTruncated = false;
  if (note) {
    ctx.font = '30px Raleway, -apple-system, BlinkMacSystemFont, sans-serif';
    var noteMaxW = cardW - 48;
    var words = note.split(' ');
    var line = '';
    var noteLines = 1;
    for (var i = 0; i < words.length; i++) {
      var test = line + (line ? ' ' : '') + words[i];
      if (ctx.measureText(test).width > noteMaxW && line) {
        noteLines++;
        line = words[i];
      } else {
        line = test;
      }
    }
    if (noteLines > maxNoteLines) noteTruncated = true;
    noteLines = Math.min(noteLines, maxNoteLines);
    var moreH = noteTruncated ? 34 : 0;
    noteH = noteLines * 38 + 28 + moreH;
  }

  // ArtworkBlock total: header + artwork + border overhead
  var artBlockBorder = 2;
  var artBlockBorderThick = 4; // right/bottom
  var artBlockRadius = 16; // 8px at 2x
  var artBlockH = headerH + artSize;

  var totalH = userRowH + artBlockH + artBlockBorderThick + artInset + noteH + 20;

  if (opts.compact) {
    // Compact: no card bg, just the artwork block directly
    totalH = artBlockH + artBlockBorderThick;
  }

  if (!opts.compact) {
    // --- Draw card background ---
    ctx.shadowColor = 'rgba(0,0,0,0.12)';
    ctx.shadowBlur = 6;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 2;
    ctx.fillStyle = 'white';
    roundRect(ctx, cardX, cardY, cardW, totalH, cardRadius);
    ctx.fill();
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
  }

  // --- Clip card ---
  ctx.save();
  if (!opts.compact) {
    roundRect(ctx, cardX, cardY, cardW, totalH, cardRadius);
    ctx.clip();
  }

  var cursorY = cardY;

  // === User row ===
  if (username) {
    var avatarSize = 40;
    var avatarX = cardX + artInset;
    var avatarY = cursorY + (userRowH - avatarSize) / 2;

    if (profileImg) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(avatarX + avatarSize / 2, avatarY + avatarSize / 2, avatarSize / 2, 0, Math.PI * 2);
      ctx.clip();
      ctx.drawImage(profileImg, avatarX, avatarY, avatarSize, avatarSize);
      ctx.restore();
    } else {
      ctx.fillStyle = avatarColor(username);
      ctx.beginPath();
      ctx.arc(avatarX + avatarSize / 2, avatarY + avatarSize / 2, avatarSize / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = 'white';
      ctx.font = 'bold 20px Raleway, -apple-system, BlinkMacSystemFont, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(
        username.charAt(0).toUpperCase(),
        avatarX + avatarSize / 2,
        avatarY + avatarSize / 2
      );
      ctx.textBaseline = 'alphabetic';
    }

    ctx.fillStyle = '#111';
    ctx.font = '900 24px Raleway, -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(username, avatarX + avatarSize + 10, avatarY + avatarSize / 2 + 8);
    cursorY += userRowH;
  }

  // === ArtworkBlock (inset in full mode, full width in compact) ===
  var abX = opts.compact ? cardX : cardX + artInset;
  var abY = cursorY;
  var abW = opts.compact ? cardW : artBlockW;
  var abArtSize = abW; // artwork is always square, matching block width
  var abH = headerH + abArtSize;

  // ArtworkBlock background (clip for rounded corners)
  ctx.save();
  roundRect(ctx, abX, abY, abW, abH, artBlockRadius);
  ctx.clip();

  // MediaHeader (black bar)
  ctx.fillStyle = 'black';
  ctx.fillRect(abX, abY, abW, headerH);

  var textX = abX + headerPad;
  var textMaxW = abW - headerPad * 2;
  var textY = abY + headerPad + headerLineH * 0.75;

  ctx.fillStyle = 'white';
  ctx.font = '900 32px Raleway, -apple-system, BlinkMacSystemFont, sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText(truncateText(ctx, artist, textMaxW), textX, textY);

  textY += headerLineH;
  ctx.font = '300 italic 30px Raleway, -apple-system, BlinkMacSystemFont, sans-serif';
  ctx.fillText(truncateText(ctx, title, textMaxW), textX, textY);

  if (isSong && albumTitle) {
    textY += headerLineH;
    ctx.font = '300 28px Raleway, -apple-system, BlinkMacSystemFont, sans-serif';
    var albumText = albumTitle;
    if (year) albumText += ' (' + year + ')';
    ctx.fillText(truncateText(ctx, albumText, textMaxW), textX, textY);
  } else if (year) {
    ctx.font = '300 28px Raleway, -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.globalAlpha = 0.6;
    ctx.fillText(
      ' (' + year + ')',
      textX + Math.min(ctx.measureText(title).width, textMaxW - 60),
      textY
    );
    ctx.globalAlpha = 1;
  }

  // Artwork image (square, matching block width)
  var artImgY = abY + headerH;
  if (artImg) {
    ctx.drawImage(artImg, abX, artImgY, abArtSize, abArtSize);
  } else {
    ctx.fillStyle = '#eee';
    ctx.fillRect(abX, artImgY, abArtSize, abArtSize);
  }

  ctx.restore(); // unclip artwork block

  // ArtworkBlock border: 1px solid black, 2px right/bottom (doubled for canvas)
  ctx.strokeStyle = 'black';
  ctx.lineWidth = artBlockBorder;
  roundRect(ctx, abX, abY, abW, abH, artBlockRadius);
  ctx.stroke();

  // Thicker right + bottom border
  ctx.lineWidth = artBlockBorderThick;
  ctx.beginPath();
  ctx.moveTo(abX + abW, abY + artBlockRadius);
  ctx.lineTo(abX + abW, abY + abH - artBlockRadius);
  ctx.quadraticCurveTo(abX + abW, abY + abH, abX + abW - artBlockRadius, abY + abH);
  ctx.lineTo(abX + artBlockRadius, abY + abH);
  ctx.quadraticCurveTo(abX, abY + abH, abX, abY + abH - artBlockRadius);
  ctx.stroke();

  cursorY = abY + abH + artBlockBorderThick;

  // === Note text (max 2 lines + "more") ===
  if (note) {
    var noteX = cardX + 24;
    var noteY2 = cursorY + 12;
    ctx.fillStyle = '#222';
    ctx.font = '30px Raleway, -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.textAlign = 'left';
    var noteMaxW2 = cardW - 48;
    var words2 = note.split(' ');
    var currentLine = '';
    var drawnLines = 0;
    for (var j = 0; j < words2.length && drawnLines < maxNoteLines; j++) {
      var test2 = currentLine + (currentLine ? ' ' : '') + words2[j];
      if (ctx.measureText(test2).width > noteMaxW2 && currentLine) {
        if (drawnLines === maxNoteLines - 1) {
          // Last allowed line — truncate with ellipsis
          ctx.fillText(
            truncateText(ctx, currentLine, noteMaxW2),
            noteX,
            noteY2 + drawnLines * 38 + 28
          );
          drawnLines++;
          currentLine = '';
          break;
        }
        ctx.fillText(currentLine, noteX, noteY2 + drawnLines * 38 + 28);
        drawnLines++;
        currentLine = words2[j];
      } else {
        currentLine = test2;
      }
    }
    if (currentLine && drawnLines < maxNoteLines) {
      ctx.fillText(truncateText(ctx, currentLine, noteMaxW2), noteX, noteY2 + drawnLines * 38 + 28);
      drawnLines++;
    }
    if (noteTruncated) {
      ctx.fillStyle = '#999';
      ctx.font = '300 28px Raleway, -apple-system, BlinkMacSystemFont, sans-serif';
      ctx.fillText('more', noteX, noteY2 + drawnLines * 38 + 24);
    }
  }

  ctx.restore(); // unclip card

  return cardY + totalH;
}

function isLightColor(color) {
  var temp = document.createElement('canvas');
  temp.width = 1;
  temp.height = 1;
  var tctx = temp.getContext('2d');
  tctx.fillStyle = color;
  tctx.fillRect(0, 0, 1, 1);
  var d = tctx.getImageData(0, 0, 1, 1).data;
  return d[0] * 0.299 + d[1] * 0.587 + d[2] * 0.114 > 140;
}

// format: "post" (1080x1080) or "story" (1080x1920)
export function generatePostShareImage(opts, format, bgColor) {
  var w = 1080;
  var h = format === 'story' ? 1920 : 1440;
  var canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  var ctx = canvas.getContext('2d');

  var artSrc = opts.artworkURL;
  if (artSrc && artSrc.startsWith('/')) artSrc = API_BASE + artSrc;

  var picSrc = opts.profilePicURL;
  if (picSrc && picSrc.startsWith('/')) picSrc = API_BASE + picSrc;

  var artPromise = artSrc
    ? loadImage(artSrc).catch(function () {
        return null;
      })
    : Promise.resolve(null);
  var picPromise = picSrc
    ? loadImage(picSrc).catch(function () {
        return null;
      })
    : Promise.resolve(null);

  return Promise.all([artPromise, picPromise]).then(function (images) {
    var artImg = images[0];
    var profileImg = images[1];

    // Background
    var bg = bgColor || '#f5f5f5';
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);

    // Card dimensions
    var cardW = format === 'story' ? 940 : 920;
    var cardX = (w - cardW) / 2;
    var cardY = format === 'story' ? 240 : 60;

    var cardBottom = drawPostCard(ctx, opts, artImg, profileImg, cardX, cardY, cardW);

    // Branding at bottom — contrast with bg
    var logoColor = isLightColor(bg) ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.8)';
    if (format === 'story') {
      drawHeavyRotationLogo(ctx, w / 2, h - 80, logoColor, 56);
    } else {
      var logoY = Math.max(cardBottom + 50, h - 60);
      drawHeavyRotationLogo(ctx, w / 2, logoY, logoColor, 40);
    }

    return canvas.toDataURL('image/png');
  });
}

// format: "post" (1080x1440) or "story" (1080x1920)
export function generateProfileShareImage(opts, format, bgColor) {
  var profilePicURL = opts.profilePicURL;
  var displayName = opts.displayName || '';
  var username = opts.username || '';
  var recentArtwork = opts.recentArtwork || [];

  var w = 1080;
  var h = format === 'story' ? 1920 : 1440;
  var canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  var ctx = canvas.getContext('2d');

  var picSrc = profilePicURL;
  if (picSrc && picSrc.startsWith('/')) picSrc = API_BASE + picSrc;

  // Load profile pic + recent artwork
  var imgPromise = picSrc
    ? loadImage(picSrc).catch(function () {
        return null;
      })
    : Promise.resolve(null);
  var artPromises = recentArtwork.map(function (src) {
    var url = src;
    if (url && url.startsWith('/')) url = API_BASE + url;
    return loadImage(url).catch(function () {
      return null;
    });
  });

  return Promise.all([imgPromise].concat(artPromises)).then(function (images) {
    var picImg = images[0];
    var artImages = images.slice(1).filter(Boolean);

    var bg = bgColor || '#1a1a1a';
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);

    // Draw collage grid background (only if useCollage flag is set)
    if (artImages.length > 0 && opts.useCollage !== false) {
      var cols = 3;
      var tileSize = Math.ceil(w / cols);
      var rows = Math.ceil(h / tileSize);
      for (var row = 0; row < rows; row++) {
        for (var col = 0; col < cols; col++) {
          var idx = (row * cols + col) % artImages.length;
          var img = artImages[idx];
          if (img) {
            ctx.drawImage(img, col * tileSize, row * tileSize, tileSize, tileSize);
          }
        }
      }
      // Slight overlay to soften collage behind card
      ctx.fillStyle = 'rgba(0, 0, 0, 0.25)';
      ctx.fillRect(0, 0, w, h);
    }

    // --- Measure card contents ---
    var logoSize = 40;
    var picSize = format === 'story' ? 300 : 260;
    var cardPad = 60;
    var picToName = 50;
    var nameH = 52;
    var usernameH = displayName ? 55 : 0;
    var ctaGap = displayName ? 40 : 30;
    var ctaH = 32;
    var logoGap = 40;
    var logoH = logoSize;
    var cardH =
      cardPad + picSize + picToName + nameH + usernameH + ctaGap + ctaH + logoGap + logoH + cardPad;

    // Measure logo width to ensure card is wide enough
    ctx.font = '800 ' + logoSize + 'px Raleway, -apple-system, BlinkMacSystemFont, sans-serif';
    var heavyMW = ctx.measureText('Heavy').width;
    ctx.font =
      '300 italic ' + logoSize + 'px Raleway, -apple-system, BlinkMacSystemFont, sans-serif';
    var rotMW = ctx.measureText('Rotation').width;
    var appMW = ctx.measureText('.app').width;
    var logoTotalW = heavyMW + 4 + rotMW + appMW + 80; // 80 padding
    var cardW = Math.max(logoTotalW, format === 'story' ? 700 : 660);
    var cardX = (w - cardW) / 2;
    var cardY = (h - cardH) / 2 - (format === 'story' ? 40 : 20);
    var cardRadius = 32;

    // Card shadow + background
    ctx.shadowColor = 'rgba(0,0,0,0.35)';
    ctx.shadowBlur = 40;
    ctx.shadowOffsetY = 8;
    ctx.fillStyle = 'white';
    roundRect(ctx, cardX, cardY, cardW, cardH, cardRadius);
    ctx.fill();
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;

    // Profile pic centered in card
    var picX = (w - picSize) / 2;
    var picY = cardY + cardPad;

    if (picImg) {
      ctx.save();
      roundRect(ctx, picX, picY, picSize, picSize, 16);
      ctx.clip();
      ctx.drawImage(picImg, picX, picY, picSize, picSize);
      ctx.restore();

      ctx.strokeStyle = 'rgba(0,0,0,0.08)';
      ctx.lineWidth = 2;
      roundRect(ctx, picX, picY, picSize, picSize, 16);
      ctx.stroke();
    } else {
      ctx.fillStyle = avatarColor(username);
      ctx.beginPath();
      ctx.arc(w / 2, picY + picSize / 2, picSize / 2, 0, Math.PI * 2);
      ctx.fill();
      if (username) {
        ctx.fillStyle = 'white';
        ctx.font = '900 90px Raleway, -apple-system, BlinkMacSystemFont, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(username.charAt(0).toUpperCase(), w / 2, picY + picSize / 2);
        ctx.textBaseline = 'alphabetic';
      }
    }

    var curY = picY + picSize + picToName;
    ctx.textAlign = 'center';
    if (displayName) {
      ctx.fillStyle = '#111';
      ctx.font = '900 52px Raleway, -apple-system, BlinkMacSystemFont, sans-serif';
      ctx.fillText(truncateText(ctx, displayName, cardW - 80), w / 2, curY + nameH * 0.8);
      curY += nameH;
      ctx.fillStyle = '#888';
      ctx.font = '300 38px Raleway, -apple-system, BlinkMacSystemFont, sans-serif';
      ctx.fillText('@' + username, w / 2, curY + 38 * 0.8);
      curY += usernameH;
    } else {
      ctx.fillStyle = '#111';
      ctx.font = '900 52px Raleway, -apple-system, BlinkMacSystemFont, sans-serif';
      ctx.fillText('@' + username, w / 2, curY + nameH * 0.8);
      curY += nameH;
    }

    curY += ctaGap;
    ctx.fillStyle = '#666';
    ctx.font = '300 32px Raleway, -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.fillText('Follow me to discover and share music', w / 2, curY + ctaH * 0.8);

    // Logo inside card at bottom
    curY += ctaH + logoGap;
    drawHeavyRotationLogo(ctx, w / 2, curY + logoH * 0.8, '#111', logoSize);

    return canvas.toDataURL('image/png');
  });
}

export function downloadDataURL(dataURL, filename) {
  var name = filename || 'share.png';

  // On mobile, use Web Share API to trigger native share sheet (allows Save Image to Photos)
  if (navigator.share && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
    fetch(dataURL)
      .then(function (res) {
        return res.blob();
      })
      .then(function (blob) {
        var file = new File([blob], name, { type: 'image/png' });
        return navigator.share({ files: [file] });
      })
      .catch(function () {});
    return;
  }

  var link = document.createElement('a');
  link.download = name;
  link.href = dataURL;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
